import React, { useRef, useState, useEffect, forwardRef } from "react";
import { NavLink } from "react-router";
import { RoutesPaths, PROFILE_UPDATED_EVENT } from "../Router/constant";
import { getAdminProfile } from "../Services/userService";
import { getNotifications, getPartnerNotifications, markNotificationAsRead } from "../Services/dashboardService";
import { useNavigate } from "react-router-dom";
import {
  FiBell,
  FiBellOff,
  FiDollarSign,
  FiFileText,
  FiHelpCircle,
  FiPackage,
  FiTruck,
  FiUserPlus,
  FiX,
} from "react-icons/fi";

const Header = forwardRef(({ onHeaderClick }, ref) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const notificationIconRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);
  const [notificationTab, setNotificationTab] = useState("all");
  const userRole = localStorage.getItem("userRole")

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getAdminProfile();
        const adminData = res.data?.data?.admin || res?.data?.data?.user;

        const admin = Array.isArray(adminData) ? adminData[0] : adminData;
        setProfileImage(admin?.profileImage || "");
      } catch (err) {
        console.error("Error fetching profile:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchNotifications = async () => {
      try {
        let res;
        if (userRole === "admin") {
          res = await getNotifications();
        } else {
          res = await getPartnerNotifications();
        }
        if (res?.data?.success) {
          setNotifications(res.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
      } finally {
        setLoadingNotifications(false);
      }
    };

    fetchProfile();
    fetchNotifications();
  }, []);

  // Update the avatar as soon as the profile is saved (no page refresh needed).
  useEffect(() => {
    const handleProfileUpdated = (event) => {
      const image = event?.detail?.profileImage;
      if (image !== undefined) setProfileImage(image || "");
    };

    window.addEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdated);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        notificationIconRef.current &&
        !notificationIconRef.current.contains(e.target)
      ) {
        setShowNotifications(false);
      }
    };


    const handleEscape = (e) => {
      if (e.key === "Escape") setShowNotifications(false);
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [showNotifications]);

  const toggleNotifications = () => setShowNotifications((prev) => !prev);

  const formatTimeAgo = (dateString) => {
    const seconds = Math.floor((Date.now() - new Date(dateString)) / 1000);
    const intervals = [
      { label: "year", seconds: 31536000 },
      { label: "month", seconds: 2592000 },
      { label: "day", seconds: 86400 },
      { label: "hour", seconds: 3600 },
      { label: "minute", seconds: 60 },
      { label: "second", seconds: 1 },
    ];

    for (const { label, seconds: s } of intervals) {
      const count = Math.floor(seconds / s);
      if (count >= 1) return `${count} ${label}${count > 1 ? "s" : ""} ago`;
    }

    return "just now";
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 10: return "New user registered";
      case 9: return "Order assigned";
      case 8: return "Contract uploaded";
      case 7: return "Quotation created";
      case 5: return "Order created";
      case 4: return "Support request";
      default: return "General notification";
    }
  };


  const handleNotificationClick = async (item) => {
    setSelectedNotificationId(item._id);
    setShowNotifications(false);
    if (!item.isRead) {
      try {
        await markNotificationAsRead(item._id);

        setNotifications((prev) =>
          prev.map((n) =>
            n._id === item._id ? { ...n, isRead: true } : n
          )
        );
      } catch (err) {
        console.error("Failed to mark notification as read", err);
      }
    }
    switch (item.type) {
      case 4:
        navigate("/contact-support");
        break;

      case 5:
        if (userRole === "admin" && item.order) {
          navigate(`/booking-management/order-history/${item.order}`);
        }
        break;

      case 8:
        if (userRole === "partner") {
          navigate("/partners/detail/agreement");
        }
        break;

      case 9:
        if (userRole === "partner") {
          navigate(`/booking-management/order-history/${item.order}`);
        }
        break;
      
      case 10:
        if (userRole === "admin") {
          navigate(RoutesPaths.USERMANAGEMENT);
        }
        break;

      default:
        break;
    }
  };


  const filteredNotifications = notifications.filter((item) => {
    if (userRole === "admin") {
      return true;
    }

    if (userRole === "partner") {
      return item.type === 4 || item.type === 8 || item.type === 9;
    }

    return false;
  });

  // Count only notifications this role can see, so the badge matches the list.
  const visibleUnreadCount = filteredNotifications.filter((n) => !n.isRead).length;
  const tabNotifications =
    notificationTab === "unread"
      ? filteredNotifications.filter((n) => !n.isRead)
      : filteredNotifications;

  const getTypeVisual = (type) => {
    switch (type) {
      case 10: return { Icon: FiUserPlus, tone: "blue" };
      case 9: return { Icon: FiTruck, tone: "purple" };
      case 8: return { Icon: FiFileText, tone: "green" };
      case 7: return { Icon: FiDollarSign, tone: "amber" };
      case 5: return { Icon: FiPackage, tone: "orange" };
      case 4: return { Icon: FiHelpCircle, tone: "rose" };
      default: return { Icon: FiBell, tone: "gray" };
    }
  };

  return (
    <div
      className={`headerSection ${userRole === "partner" ? "headerSectionPartner" : ""
        }`}
    >      <div className="mobileView" ref={ref} onClick={onHeaderClick}>
        <img src="/assets/img/threeline.svg" alt="" className="threelineIcon" />
        <img src="/assets/img/quimeraLogo.svg" alt="Quimera Logo" />
      </div>

      <div className="m-0 mobileHide">
        {/* <div className="searchSideBar m-0 mobileHide"> */}
        {/* <img src="/assets/img/search.svg" alt="" /> */}
        {/* <input type="text" placeholder="Search" /> */}
      </div>

      <div className="headerRightSec">
        {/* <div className="countryFlag mobileHide">
          <img src="https://flagcdn.com/48x36/in.png" alt="IN Flag" />
        </div> */}
        {userRole === 'admin' &&
          <div className="headerIconSection mobileHide">
            <NavLink to={RoutesPaths.USERMANAGEMENT}>
              {userRole === "partner" ? <img src="/assets/img/usersPartner.svg" alt="Users" /> : <img src="/assets/img/users.svg" alt="Users" />}
            </NavLink>
          </div>
        }

        <div
          className="headerIconSection notificationSection mobileHide"
          onClick={toggleNotifications}
          ref={notificationIconRef}
        >
          <div className="notificationIconWrapper">
            <img
              src={
                userRole === "partner"
                  ? "/assets/img/notificationPartner.svg"
                  : "/assets/img/notification.svg"
              }
              alt="Notification"
            />

            {visibleUnreadCount > 0 && (
              <span className="notificationBadge">
                {visibleUnreadCount > 99 ? "99+" : visibleUnreadCount}
              </span>
            )}
          </div>
        </div>


        <NavLink to={RoutesPaths.PROFILE}>
          <div className="userImage">
            {loading ? (
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "#e0e0e0",
                  animation: "pulse 1.5s infinite",
                }}
              />
            ) : (
              <img
                src={
                  profileImage
                    ? `${process.env.REACT_APP_IMAGE_URL}/${profileImage}`
                    : "/assets/img/download.png"
                }
                alt="User"
              />
            )}

          </div>
        </NavLink>

        {showNotifications && (
          <div
            className="userDropdown qnt-panel"
            ref={dropdownRef}
            role="dialog"
            aria-label="Notifications"
          >
            <span className="qnt-arrow" aria-hidden="true" />

            <div className="qnt-head">
              <div className="qnt-title">
                <h4>Notifications</h4>
                {visibleUnreadCount > 0 && (
                  <span className="qnt-count">{visibleUnreadCount} new</span>
                )}
              </div>
              <button
                type="button"
                className="qnt-close"
                onClick={() => setShowNotifications(false)}
                aria-label="Close notifications"
              >
                <FiX />
              </button>
            </div>

            <div className="qnt-tabs" role="tablist">
              {[
                { value: "all", label: "All", count: filteredNotifications.length },
                { value: "unread", label: "Unread", count: visibleUnreadCount },
              ].map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={notificationTab === tab.value}
                  className={notificationTab === tab.value ? "active" : ""}
                  onClick={() => setNotificationTab(tab.value)}
                >
                  {tab.label}
                  <span>{tab.count}</span>
                </button>
              ))}
            </div>

            <div className="qnt-list">
              {loadingNotifications ? (
                [...Array(4)].map((_, i) => (
                  <div className="qnt-item qnt-skel" key={i} aria-hidden="true">
                    <span className="qd-skel qnt-skel-icon" />
                    <span className="qnt-skel-lines">
                      <span className="qd-skel" style={{ width: "70%", height: 12 }} />
                      <span className="qd-skel" style={{ width: "45%", height: 10 }} />
                    </span>
                  </div>
                ))
              ) : tabNotifications.length > 0 ? (
                tabNotifications.map((item, index) => {
                  const { Icon, tone } = getTypeVisual(item.type);
                  return (
                    <button
                      type="button"
                      key={item._id || index}
                      className={`qnt-item ${!item.isRead ? "unread" : ""} ${
                        selectedNotificationId === item._id ? "selected" : ""
                      }`}
                      onClick={() => handleNotificationClick(item)}
                    >
                      <span className="qnt-avatar">
                        {item?.user?.profileImage ? (
                          <img
                            src={`${process.env.REACT_APP_IMAGE_URL}/${item.user.profileImage}`}
                            alt=""
                          />
                        ) : (
                          <span className={`qnt-type-icon qnt-tone-${tone}`}>
                            <Icon />
                          </span>
                        )}
                        {item?.user?.profileImage && (
                          <span className={`qnt-type-badge qnt-tone-${tone}`}>
                            <Icon />
                          </span>
                        )}
                      </span>
                      <span className="qnt-body">
                        <span className="qnt-row">
                          <strong>{getTypeLabel(item.type)}</strong>
                          <small>{formatTimeAgo(item.createdAt)}</small>
                        </span>
                        <span className="qnt-email">{item.email || "—"}</span>
                      </span>
                      {!item.isRead && <span className="qnt-dot" aria-label="Unread" />}
                    </button>
                  );
                })
              ) : (
                <div className="qnt-empty">
                  <span className="qnt-empty-art">
                    <FiBellOff aria-hidden="true" />
                  </span>
                  <strong>
                    {notificationTab === "unread" ? "You're all caught up" : "No notifications yet"}
                  </strong>
                  <span>
                    {notificationTab === "unread"
                      ? "There are no unread notifications."
                      : "New activity will show up here."}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default Header;
